import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Phone, Mail, ArrowUpRight, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "reformas",
    scale: "medium",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const serviceOptions = [...t.services.items.map((s) => ({ id: s.id, label: s.title })), { id: "otro", label: t.quote.serviceOther }];
  const scaleOptions = Object.entries(t.quote.scales).map(([id, label]) => ({ id, label }));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const waLink = `https://wa.me/34622669378?text=${encodeURIComponent(t.wa.prefill)}`;

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, lang });
      setSent(true);
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-black/15 bg-bone px-4 py-3 text-sm text-charcoal placeholder:text-stone-400 focus:border-terracotta focus:outline-none transition-colors duration-300";

  return (
    <section
      id="presupuesto"
      data-testid="quote-section"
      className="relative overflow-hidden bg-charcoal py-28 text-stone-300 lg:py-40"
    >
      <div className="bg-blueprint absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 lg:grid-cols-5 lg:gap-20 lg:px-10">
        <Reveal className="lg:col-span-2">
          <p
            data-testid="quote-eyebrow"
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta"
          >
            {t.quote.eyebrow}
          </p>
          <h2
            data-testid="quote-heading"
            className="mt-5 font-display text-3xl font-medium leading-tight tracking-tight text-bone sm:text-4xl"
          >
            {t.quote.heading}
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-stone-400">
            {t.quote.sub}
          </p>

          <ul className="mt-10 space-y-4" data-testid="quote-points">
            {t.quote.points.map((p, i) => (
              <li key={`${lang}-pt-${i}`} className="flex items-start gap-3 text-sm text-stone-300">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-terracotta/60 text-terracotta">
                  <Check size={12} />
                </span>
                {p}
              </li>
            ))}
          </ul>

          <div className="mt-12 space-y-3 border-t border-white/10 pt-8">
            <a
              data-testid="quote-phone-link"
              href="tel:+34622669378"
              className="flex items-center gap-3 font-mono text-sm tracking-widest text-stone-300 transition-colors hover:text-terracotta"
            >
              <Phone size={15} className="text-terracotta" /> +34 622 66 93 78
            </a>
            <a
              data-testid="quote-email-link"
              href="mailto:martinezamd95@gmail.com"
              className="flex items-center gap-3 font-mono text-sm tracking-widest text-stone-300 transition-colors hover:text-terracotta"
            >
              <Mail size={15} className="text-terracotta" /> martinezamd95@gmail.com
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-3">
          <div className="relative bg-bone p-7 text-charcoal lg:p-12">
            <div className="absolute -right-3 -top-3 h-full w-full border border-terracotta/50" aria-hidden="true" />
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  data-testid="quote-success-panel"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="relative flex min-h-[26rem] flex-col items-start justify-center"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-terracotta text-bone">
                    <Check size={26} />
                  </span>
                  <h3 className="mt-8 font-display text-3xl font-medium">{t.quote.successTitle}</h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-stone-500">
                    {t.quote.successBody}
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <a
                      data-testid="quote-success-whatsapp-btn"
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#1fb857]"
                    >
                      {t.quote.whatsappCta} <ArrowUpRight size={14} />
                    </a>
                    <button
                      data-testid="quote-again-btn"
                      onClick={() => {
                        setSent(false);
                        setForm({ name: "", phone: "", email: "", service: "reformas", scale: "medium", message: "" });
                      }}
                      className="rounded-full border border-black/15 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-stone-600 transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
                    >
                      {t.quote.again}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="quote-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={submit}
                  className="relative space-y-7"
                >
                  <div className="grid gap-7 sm:grid-cols-2">
                    <div>
                      <label htmlFor="q-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                        {t.quote.name} *
                      </label>
                      <input
                        id="q-name"
                        data-testid="quote-name-input"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder={t.quote.namePh}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="q-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                        {t.quote.phone} *
                      </label>
                      <input
                        id="q-phone"
                        data-testid="quote-phone-input"
                        required
                        minLength={6}
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder={t.quote.phonePh}
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="q-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                      {t.quote.email}
                    </label>
                    <input
                      id="q-email"
                      data-testid="quote-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder={t.quote.emailPh}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <span className="mb-3 block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                      {t.quote.service}
                    </span>
                    <div className="flex flex-wrap gap-2" data-testid="quote-service-options">
                      {serviceOptions.map((o) => (
                        <button
                          type="button"
                          key={o.id}
                          data-testid={`quote-service-${o.id}`}
                          onClick={() => set("service", o.id)}
                          className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                            form.service === o.id
                              ? "border-terracotta bg-terracotta text-bone"
                              : "border-black/15 text-stone-600 hover:border-terracotta hover:text-terracotta"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className="mb-3 block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                      {t.quote.scale}
                    </span>
                    <div className="flex flex-wrap gap-2" data-testid="quote-scale-options">
                      {scaleOptions.map((o) => (
                        <button
                          type="button"
                          key={o.id}
                          data-testid={`quote-scale-${o.id}`}
                          onClick={() => set("scale", o.id)}
                          className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                            form.scale === o.id
                              ? "border-charcoal bg-charcoal text-bone"
                              : "border-black/15 text-stone-600 hover:border-charcoal"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="q-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
                      {t.quote.message}
                    </label>
                    <textarea
                      id="q-message"
                      data-testid="quote-message-input"
                      rows={4}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder={t.quote.messagePh}
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  <button
                    data-testid="quote-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="group flex w-full items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-terra-dark disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <Loader2 size={15} className="animate-spin" /> {t.quote.sending}
                      </>
                    ) : (
                      <>
                        {t.quote.submit}
                        <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default QuoteForm;
